import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import moment from "moment";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";

import {
  FaEdit,
  FaCheck,
  FaTimes,
  FaPlus,
  FaChevronDown,
  FaChevronUp,
  FaMoneyBillWave,
  FaHistory,
  FaListUl,
  FaReceipt,
  FaFileAlt,
  FaArrowLeft,
  FaTrash,
} from "react-icons/fa";

import { getPurchaseOrderById } from "../../../../Redux/actions/Operationsactions/getpurchaseorderbyid";
import {
  DeletePurchaseOrderApi,
  UpdateGrnApprovalApi,
  UpdateApprovalApi,
} from "../../../../Api/Operations/PurchaseOrders";
import { useSession } from "../../../../context/SessionContext";
import StatusChip, { statusChipType } from "../../../Common/StatusChip";
import DeleteConfirmModal from "../../../../Common/DeleteConfirmModal";
import { fetchGeneralLedgerTransactions } from "../../../../Redux/actions/AccountReports/reports";
import TransactionDetailModal from "../../../../Common/TransactionDetailModal";
import ActionButton from "../../../Common/ActionButton";
import GenerateGrnModal from "./GenerateGrnModal";

const StatusBadge = ({ status }) => <StatusChip status={status} type={statusChipType(status)} />;

const ColoredActionButton = ({
  icon: Icon,
  label,
  onClick,
  variant = "primary",
  disabled = false,
}) => {
  const variants = {
    primary: { bg: "#0b6462", hover: "#095250", shadow: "rgba(11,100,98,0.25)", text: "white" },
    danger: { bg: "#c0392b", hover: "#a93226", shadow: "rgba(192,57,43,0.25)", text: "white" },
    success: { bg: "#0e8c6e", hover: "#0a7058", shadow: "rgba(14,140,110,0.25)", text: "white" },
    info: { bg: "#0097a7", hover: "#00838f", shadow: "rgba(0,151,167,0.25)", text: "white" },
    warning: { bg: "#e07b00", hover: "#c96e00", shadow: "rgba(224,123,0,0.25)", text: "white" },
    secondary: { bg: "#546e7a", hover: "#455a64", shadow: "rgba(84,110,122,0.25)", text: "white" },
    save: { bg: "#0b6462", hover: "#095250", shadow: "rgba(11,100,98,0.25)", text: "white" },
  };

  const v = variants[variant] || variants.primary;

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="btn d-flex align-items-center justify-content-center"
      style={{
        padding: "6px 12px",
        borderRadius: "8px",
        fontSize: "12px",
        fontWeight: "600",
        whiteSpace: "nowrap",
        transition: "all 0.2s ease",
        backgroundColor: v.bg,
        color: v.text,
        border: "none",
        boxShadow: `0 2px 8px ${v.shadow}`,
        opacity: disabled ? 0.55 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        gap: "5px",
      }}
      title={label}
      onMouseEnter={(e) => {
        if (disabled) {
          return;
        }
        e.currentTarget.style.backgroundColor = v.hover;
        e.currentTarget.style.boxShadow = `0 4px 12px ${v.shadow}`;
      }}
      onMouseLeave={(e) => {
        if (disabled) {
          return;
        }
        e.currentTarget.style.backgroundColor = v.bg;
        e.currentTarget.style.boxShadow = `0 2px 8px ${v.shadow}`;
      }}
    >
      <Icon size={12} />
      <span className="d-none d-md-inline">{label}</span>
    </button>
  );
};

const SectionHeader = ({ icon: Icon, title, rightElement }) => (
  <div
    className="d-flex align-items-center justify-content-between mb-3 pb-2"
    style={{ borderBottom: "2px solid #e7f4f4" }}
  >
    <div className="d-flex align-items-center">
      <div
        className="d-flex align-items-center justify-content-center me-2"
        style={{
          width: "36px",
          height: "36px",
          borderRadius: "10px",
          backgroundColor: "#e7f4f4",
          color: "#0b6462",
        }}
      >
        <Icon size={20} />
      </div>
      <h6 className="m-0 ml-2 " style={{ fontWeight: "700", letterSpacing: "-0.5px" }}>
        {title}
      </h6>
    </div>
    {rightElement}
  </div>
);

const DetailRow = ({ type, label, value, name }) => {
  // if (!isVal(value)) return null;
  if (type === "description") {
    return (
      <div className="col-12 mb-2">
        <div className="text-secondary fz-12" style={{ fontWeight: 500, marginBottom: "2px" }}>
          {label}
        </div>
        <div className="text-dark fz-13" style={{ fontWeight: 600, wordBreak: "break-word" }}>
          {value}
        </div>
      </div>
    );
  }
  if (type === "file") {
    return (
      <div className="col-12 mb-2">
        <div className="text-secondary fz-12" style={{ fontWeight: 500, marginBottom: "2px" }}>
          {label}
        </div>
        <div className="text-dark fz-13" style={{ fontWeight: 600, wordBreak: "break-word" }}>
          {value ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="text-decoration-none text-primary"
            >
              {name || "Preview file"}
            </a>
          ) : (
            "No file"
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="col-6 mb-2">
      <div className="text-secondary fz-12" style={{ fontWeight: 500, marginBottom: "2px" }}>
        {label}
      </div>
      <div className="text-dark fz-13" style={{ fontWeight: 600, wordBreak: "break-word" }}>
        {value}
      </div>
    </div>
  );
};

export default function PurchaseOrderDetailsPage() {
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const { session } = useSession();

  const reduxState = useSelector((s) => s.getPurchaseOrderDetails || { data: null, loading: true });
  const generalLedger = useSelector((state) => state.generalLedger);
  const rolesData = useSelector((s) => s.GetAllRole);

  const [isLoading, setIsLoading] = useState(false);

  const [showGrnModal, setShowGrnModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [grnApproval, setGrnApproval] = useState(false);
  const [confirmationType, setConfirmationType] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [openTransactionModel, setOpenTransactionModel] = useState(false);
  const [approveComments, setApproveComments] = useState("");
  const [selectedApprovalId, setSelectedApprovalId] = useState(null);
  const [expandedGrns, setExpandedGrns] = useState({});
  const [grnReject, setGrnReject] = useState(false);

  const toggleGrn = (grnId) => {
    setExpandedGrns((prev) => ({ ...prev, [grnId]: !prev[grnId] }));
  };

  useEffect(() => {
    if (!id) {
      return;
    }
    dispatch(getPurchaseOrderById(id));
  }, [id, dispatch]);

  const current = reduxState?.data?.Data || null;

  const openEdit = useCallback(() => {
    history.push("/procurement/purchase-order/add", current);
  }, [history, current]);
  const getTransactions = (id) => {
    dispatch(fetchGeneralLedgerTransactions(id));
  };

  const openApprove = (approvals) => {
    if (!approvals.length) {
      return;
    }
    // find a pending approval entry
    const pending = (approvals || []).find(
      (a) => a?.ApproverUserId === session?.LoginId && a?.Status === "Pending",
    );
    const approvalId = pending?.Id;
    if (!approvalId) {
      // if approvals not present, try top-level approval id
      toast.info("No pending approval found");
      return;
    }
    setSelectedApprovalId(approvalId);
    setApproveComments("");
    setShowApproveModal(true);
  };

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const res = await DeletePurchaseOrderApi(current?.Id || current?.PurchaseOrderId);
      if (res?.data?.IsSuccess) {
        toast.success("Purchase Order deleted");
        history.push("/store/vendors/purchase-order");
      } else {
        toast.error(res?.data?.Message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error deleting purchase order");
    } finally {
      setIsLoading(false);
      setShowConfirmationModal(false);
      setConfirmationType(null);
    }
  };

  const activityRows = useMemo(() => {
    // Prefer an explicit Activities/History array from API if present
    if (!current) {
      return [];
    }
    if (Array.isArray(current.Activities) && current.Activities.length) {
      return current.Activities.map((a) => ({
        dateTime: a.DateTime || a.CreatedAt || a.Date,
        action: a.Action || a.Type,
        description: a.Description || a.Note || "",
        user: a.User || a.UserName || a.CreatedBy || "",
      }));
    }
    // Fallback: construct timeline from approvals and GRNs
    const rows = [];
    if (current.PoDate) {
      rows.push({
        dateTime: current.CreatedAt,
        action: `Created: ${current.PoNumber}`,
        description: `Purchase Order Created`,
        user: current.CreatedBy,
      });
    }
    (current.Approvals || []).forEach((a) =>
      rows.push({
        dateTime: a.ActionDate || null,
        action: `${a.Status?.toUpperCase() === "PENDING" ? "Pending Approval" : a.Status}: ${current.PoNumber}`,
        description: a.Comments || "",
        user: a.ApprovalUserName,
      }),
    );
    (current.GRNs || []).forEach((g) => {
      rows.push({
        dateTime: g.CreatedAt,
        action: `Created: ${g.GrnNumber}`,
        description: `GRN created`,
        user: g.CreatedBy,
      });
      // Add Payment created
      if (g.Status === "Completed") {
        rows.push({
          dateTime: g.PaymentCreated || g.UpdatedAt || g.CreatedAt,
          action: "Payment Created",
          description: `Payment created for ${g.GrnNumber}`,
          user: g.CreatedBy,
        });
      }
      // Add Payment Approval
      g.Approvals?.forEach((a) =>
        rows.push({
          dateTime: a.ActionDate || null,
          action: `${a.Status?.toUpperCase() === "PENDING" ? "Pending Approval" : a.Status} : ${g.GrnNumber}`,
          description: a.Comments || "",
          user: a.ApprovalUserName,
        }),
      );
    });
    return rows.sort((x, y) => new Date(y.dateTime) - new Date(x.dateTime));
  }, [current]);

  const activityColDefs = useMemo(
    () => [
      {
        headerName: "Date/Time",
        field: "dateTime",
        flex: 1,
        valueFormatter: (p) => (p.value ? moment(p.value).format("YYYY-MM-DD HH:mm") : ""),
      },
      { headerName: "Action", field: "action", flex: 1 },
      { headerName: "Description", field: "description", flex: 2 },
      { headerName: "User", field: "user", flex: 1 },
    ],
    [],
  );

  const handleApproveGrn = async () => {
    setIsLoading(true);
    const status = grnReject ? "Rejected" : "Approved";
    try {
      const res = await UpdateGrnApprovalApi(selectedApprovalId, status, approveComments || "");
      if (res?.data?.IsSuccess) {
        toast.success(`GRN ${status.toLowerCase()}`);
        setShowApproveModal(false);
        setSelectedApprovalId(null);
        setApproveComments("");
        dispatch(getPurchaseOrderById(id));
      } else {
        toast.error(
          res?.data?.Message || `Failed to ${status === "Rejected" ? "reject" : "approve"} GRN`,
        );
      }
    } catch (err) {
      console.error(err);
      toast.error(`Error ${status === "Rejected" ? "rejecting" : "approving"} GRN`);
    } finally {
      setIsLoading(false);
      setShowApproveModal(false);
      setConfirmationType(null);
      setGrnApproval(false);
      setGrnReject(false);
    }
  };

  async function handleApproveSubmit() {
    if (!selectedApprovalId) {
      toast.info("No approval selected");
      return;
    }

    setIsLoading(true);
    try {
      const res = await UpdateApprovalApi(selectedApprovalId, "Approved", approveComments || "");
      const json = res?.data || res;
      if (json?.IsSuccess) {
        toast.success(json.Message || "Approval updated");
        setShowApproveModal(false);
        await dispatch(getPurchaseOrderById(id));
      } else {
        toast.error(json?.Message || "Failed to update approval");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating approval");
    } finally {
      setIsLoading(false);
      setShowApproveModal(false);
      setConfirmationType(null);
    }
  }

  return (
    <div className="container-fluid p-3 anim-fade-in">
      <style>
        {`
          .anim-fade-in {
            animation: fadeIn 0.4s ease-out;
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .grn-card {
            transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          .grn-card:hover {
            border-color: #0b6462 !important;
            box-shadow: 0 8px 20px rgba(11, 100, 98, 0.12) !important;
            transform: translateY(-2px);
          }
          .section-header-underline {
             position: relative;
          }
          .section-header-underline::after {
            content: '';
            position: absolute;
            bottom: -2px;
            left: 0;
            width: 60px;
            height: 3px;
            background-color: #0b6462;
            border-radius: 3px;
          }
        `}
      </style>
      <div className="row mb-3">
        <div className="col-12 page_heading">
          <div className="d-flex align-items-center">
            <FaArrowLeft
              size={16}
              style={{ cursor: "pointer", marginRight: "12px" }}
              title="Back"
              onClick={() => history.goBack()}
            />
            <h4 className="m-0">Purchase Order Details</h4>
          </div>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-md-5 mt-2">
          <div
            className="p-3 h-100"
            style={{
              backgroundColor: "#fafafa",
              borderRadius: "6px",
              boxShadow: "0px 2px 4px rgba(0,0,0,0.04)",
              border: "1px solid #e0e0e0",
            }}
          >
            <h6
              className="section-heading mb-2 fz-15 d-flex align-items-center justify-content-between"
              style={{
                color: "#0b6462",
                borderBottom: "1px solid #caf5f5",
                paddingBottom: "8px",
                paddingTop: "4px",
                marginBottom: "10px",
              }}
            >
              <div
                className="d-flex align-items-center"
                style={{ whiteSpace: "nowrap", flexShrink: 0 }}
              >
                <div
                  className="d-flex align-items-center justify-content-center me-2"
                  style={{
                    width: "30px",
                    height: "30px",
                    borderRadius: "8px",
                    backgroundColor: "#e7f4f4",
                    color: "#0b6462",
                    flexShrink: 0,
                  }}
                >
                  <FaFileAlt size={16} />
                </div>
                <b className="ml-1" style={{ whiteSpace: "nowrap" }}>
                  {current?.PoNumber}
                </b>
              </div>
              <div
                className="ms-auto d-flex align-items-center"
                style={{ gap: "10px", marginLeft: "12px" }}
              >
                {current?.Status === "Pending Approval" &&
                  current?.Approvals?.some(
                    (a) => a.ApproverUserId === session?.LoginId && a.Status === "Pending",
                  ) && (
                    <ColoredActionButton
                      icon={FaCheck}
                      label="Approve PO"
                      variant="success"
                      onClick={() => openApprove(current?.Approvals)}
                    />
                  )}
                {current?.Status !== "Approved" && current?.GRNs.length == 0 && (
                  <ColoredActionButton
                    icon={FaEdit}
                    label="Edit PO"
                    variant="primary"
                    onClick={openEdit}
                  />
                )}

                {rolesData?.data?.UserRoles?.includes("Store Purchase Orders_Delete") &&
                  current?.Status !== "Approved" &&
                  current?.GRNs?.length === 0 && (
                    <ColoredActionButton
                      icon={FaTrash}
                      label="Delete PO"
                      variant="danger"
                      onClick={() => {
                        setShowConfirmationModal(true);
                        setConfirmationType("delete");
                      }}
                    />
                  )}
              </div>
            </h6>
            <div className="row mx-0 mt-2">
              <DetailRow label="Vendor" value={current?.VendorName} />

              <DetailRow
                label="Order Date"
                value={current?.PoDate ? moment(current.PoDate).format("DD-MMM-YYYY") : "-"}
              />
              <DetailRow
                label="Delivery Date"
                value={
                  current?.DeliveryDate ? moment(current.DeliveryDate).format("DD-MMM-YYYY") : "-"
                }
              />

              <DetailRow
                label="PO Status"
                value={<StatusBadge status={current?.Status || "N/A"} />}
              />

              <DetailRow label="Project" value={current?.ProjectName || "-"} />
              <DetailRow label="Delivery Site" value={current?.DeliverySite || "-"} />
              <DetailRow label="Payment Terms" value={current?.PaymentTerms || "-"} />
              <DetailRow
                label="Freight"
                value={`Rs. ${Number(current?.FreightAmount || 0).toLocaleString()}`}
              />
              <DetailRow
                label="Tax Amount"
                value={`Rs. ${current?.TaxAmount?.toLocaleString?.()}` || current?.TaxAmount}
              />

              <DetailRow
                label="PO Amount"
                value={`Rs. ${current?.GrossAmount?.toLocaleString?.()}` || current?.TotalAmount}
              />
              <DetailRow
                type="file"
                label="Attachment"
                value={current?.FileUrl}
                name={current?.FileName}
              />
              <DetailRow
                type="description"
                label="Description"
                value={current?.Description || "N/A"}
              />
            </div>
          </div>
        </div>
        <div className="col-md-7 mt-2">
          <SectionHeader icon={FaHistory} title="Activity History" />
          <div style={{ height: 300 }} className="ag-theme-alpine shadow-sm rounded">
            <AgGridReact
              rowData={activityRows}
              columnDefs={activityColDefs}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              pagination={true}
              paginationPageSize={10}
            />
          </div>
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-12">
          <SectionHeader icon={FaListUl} title="Purchase Order Line Items" />
          <div className="table-responsive_modal table_height-voucher mt-0 shadow-sm rounded border">
            <table className="table table-hover m-0">
              <thead style={{ backgroundColor: "#f8f9fa" }}>
                <tr>
                  <th className="py-3">Sr</th>
                  <th className="py-3">Description</th>
                  <th className="py-3">PR No</th>
                  <th className="py-3">Qty</th>
                  <th className="py-3">Remaining Qty</th>
                  <th className="py-3 text-end">Unit Price</th>
                  <th className="py-3 text-end">Total</th>
                  <th className="py-3">UOM</th>
                </tr>
              </thead>
              <tbody>
                {current?.Lines?.map((l, i) => (
                  <tr key={i}>
                    <td className="table_data">{i + 1}</td>
                    <td className="table_data">{l.ItemDescription}</td>
                    <td className="table_data">{l.PrNumber || "-"}</td>
                    <td className="table_data">{l.Quantity}</td>
                    <td className="table_data">{l?.RemainingQuantity}</td>
                    <td className="table_data text-end">
                      {l.UnitPrice?.toLocaleString?.() ?? l.UnitPrice}
                    </td>
                    <td className="table_data text-end">
                      {l.LineTotal?.toLocaleString?.() ??
                        ((l.Quantity || 0) * (l.UnitPrice || 0)).toLocaleString()}
                    </td>

                    <td className="table_data">{l.UnitOfMeasure}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-12 ">
          <SectionHeader
            icon={FaReceipt}
            title="Goods Received Notes (GRNs)"
            rightElement={
              current?.Lines?.reduce((accumulator, currentItem) => {
                return accumulator + currentItem.RemainingQuantity;
              }, 0) > 0 &&
              (current?.Status === "Approved" || current?.Status === "Pending") && (
                <ColoredActionButton
                  icon={FaPlus}
                  label="Generate GRN"
                  variant="primary"
                  onClick={() => history.push("/procurement/GRN/add", { poData: current })}
                />
              )
            }
          />

          {current?.GRNs?.length === 0 ? (
            <div className="text-center p-4 border rounded bg-light text-secondary">
              No GRNs generated yet.
            </div>
          ) : (
            current?.GRNs?.map((g) => {
              const isExpanded = !!expandedGrns[g.Id];
              return (
                <div
                  key={g.Id}
                  className="mb-3 overflow-hidden grn-card"
                  style={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    transition: "all 0.3s ease",
                  }}
                >
                  {/* Accordion Header */}
                  <div
                    className="d-flex align-items-center justify-content-between p-3"
                    style={{
                      backgroundColor: isExpanded ? "#f8fdfd" : "#fff",
                      cursor: "pointer",
                      borderBottom: isExpanded ? "1px solid #e0e0e0" : "none",
                    }}
                    onClick={() => toggleGrn(g.Id)}
                  >
                    <div className="d-flex align-items-center" style={{ gap: "20px" }}>
                      <div className="d-flex align-items-center" style={{ width: "220px" }}>
                        <div
                          className="me-2 d-flex align-items-center justify-content-center"
                          style={{
                            width: "32px",
                            height: "32px",
                            borderRadius: "50%",
                            backgroundColor: "#e7f4f4",
                            color: "#0b6462",
                          }}
                        >
                          {isExpanded ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
                        </div>
                        <span style={{ fontWeight: 700, color: "#333" }}>{g.GrnNumber}</span>
                      </div>

                      <div className="d-none d-md-block">
                        <small className="text-secondary d-block">Date</small>
                        <span style={{ fontSize: "13px", fontWeight: "500" }}>
                          {g.GrnDate ? moment(g.GrnDate).format("DD-MMM-YYYY") : "-"}
                        </span>
                      </div>

                      <div className="d-none d-md-block">
                        <small className="text-secondary d-block">Status</small>
                        <StatusBadge status={g.Status} />
                      </div>

                      <div className="d-none d-lg-block">
                        <small className="text-secondary d-block">Total Payable</small>
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: "600",
                            color: "#0b6462",
                          }}
                        >
                          {g.Status === "Rejected"
                            ? "-"
                            : `Rs. ${g.TotalReceivedAmount.toLocaleString()}`}
                        </span>
                      </div>
                    </div>

                    <div
                      className="d-flex align-items-center"
                      style={{ gap: "8px" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {g?.Approvals.find(
                        (a) => a.ApproverUserId === session?.LoginId && a.Status === "Pending",
                      ) && (
                        <ColoredActionButton
                          icon={FaCheck}
                          label="Approve GRN"
                          variant="success"
                          onClick={() => {
                            setGrnApproval(true);
                            setGrnReject(false);
                            openApprove(g?.Approvals);
                          }}
                        />
                      )}
                      {g?.Approvals.find(
                        (a) => a.ApproverUserId === session?.LoginId && a.Status === "Pending",
                      ) && (
                        <ColoredActionButton
                          icon={FaTimes}
                          label="Reject GRN"
                          variant="danger"
                          onClick={() => {
                            setGrnApproval(true);
                            setGrnReject(true);
                            openApprove(g?.Approvals);
                          }}
                        />
                      )}
                      {g.Status === "Completed" && (
                        <ColoredActionButton
                          icon={FaMoneyBillWave}
                          label="View Transactions"
                          variant="save"
                          onClick={() => {
                            getTransactions(
                              [g.GrnNumber, g.Id].filter(Boolean).join(","),
                              "PurchaseOrder",
                            );
                            setOpenTransactionModel(true);
                          }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Accordion Body */}
                  {isExpanded && (
                    <div className="p-3 bg-white anim-fade-in">
                      <div className="row mb-3 d-md-none">
                        <div className="col-6 mb-2">
                          <small className="text-secondary d-block">Status</small>
                          <StatusBadge status={g.Status} />
                        </div>
                        <div className="col-6 mb-2">
                          <small className="text-secondary d-block">Total Payable</small>
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight: "600",
                              color: "#0b6462",
                            }}
                          >
                            {g.Status === "Rejected"
                              ? "-"
                              : `Rs. ${g.TotalReceivedAmount.toLocaleString()}`}
                          </span>
                        </div>
                      </div>

                      {g.FileUrl && (
                        <div className="mb-2" style={{ fontSize: "13px" }}>
                          <span className="text-secondary">Attachment: </span>
                          <a href={g.FileUrl} target="_blank" rel="noopener noreferrer">
                            {g.FileName || "Preview file"}
                          </a>
                        </div>
                      )}

                      <div className="table-responsive shadow-sm rounded border">
                        <table className="table table-sm table-hover m-0">
                          <thead style={{ backgroundColor: "#f8f9fa" }}>
                            <tr>
                              <th className="ps-3 py-2">Sr</th>
                              <th className="py-2">Description</th>
                              <th className="py-2">Qty Received</th>
                              <th className="py-2 text-end">Payable Amount</th>
                              <th className="py-2 ps-4">Remarks</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g?.Lines?.map((l, i) => (
                              <tr key={i}>
                                <td className="ps-3 table_data">{i + 1}</td>
                                <td className="table_data">{l.ItemDescription}</td>
                                <td className="table_data">{l.QuantityReceived}</td>
                                <td className="table_data text-end">
                                  {l.ReceivedAmount?.toLocaleString?.() ?? l.ReceivedAmount}
                                </td>
                                <td className="table_data ps-4">{l.Remarks || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {showGrnModal && (
        <GenerateGrnModal
          isOpen={showGrnModal}
          onClose={() => setShowGrnModal(false)}
          poData={current}
          onSuccess={async () => {
            setShowGrnModal(false);
            await dispatch(getPurchaseOrderById(id));
          }}
        />
      )}
      {showApproveModal && (
        <div
          className="modalscreen"
          style={{ display: "block", height: "100vh", overflow: "auto" }}
        >
          <div className="modal-content-screen1">
            <div className="row sticky-topNew">
              <div className="col-12 page_heading">
                <h4 className="text-center mb-0">
                  {grnApproval
                    ? grnReject
                      ? "Reject GRN"
                      : "Approve GRN"
                    : "Approve Purchase Order"}
                </h4>
                <span className="close pr-2" onClick={() => setShowApproveModal(false)}>
                  &times;
                </span>
              </div>
            </div>
            <div className="pl-3 pr-3">
              <div className="mt-3 d-flex ">
                <label className="input_label mr-3">Comments (optional)</label>
                <textarea
                  className="input_address"
                  rows={4}
                  value={approveComments}
                  onChange={(e) => setApproveComments(e.target.value)}
                />
              </div>
              <div className="row mt-4">
                <div className="col-12 d-flex justify-content-end" style={{ gap: 8 }}>
                  <ActionButton
                    text="Cancel"
                    variant="cancel"
                    onClick={() => setShowApproveModal(false)}
                  />
                  <ActionButton
                    text={grnReject ? "Reject" : "Approve"}
                    variant="save"
                    onClick={() => {
                      if (grnApproval) {
                        handleApproveGrn();
                      } else {
                        handleApproveSubmit();
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showConfirmationModal && (
        <DeleteConfirmModal
          open={showConfirmationModal}
          onClose={() => {
            setGrnApproval(false);
            setShowConfirmationModal(false);
            setConfirmationType(null);
          }}
          onConfirm={() => {
            if (confirmationType === "delete") {
              handleDelete();
            } else if (confirmationType === "approve") {
              if (grnApproval) {
                handleApproveGrn();
              } else {
                handleApproveSubmit();
              }
            }
          }}
          message={
            confirmationType === "delete"
              ? "Do you really want to delete this purchase order?"
              : grnApproval
                ? "Do you really want to approve this GRN?"
                : "Do you really want to approve this purchase order?"
          }
          heading={
            confirmationType === "delete"
              ? "Delete Purchase Order"
              : grnApproval
                ? "Approve GRN"
                : "Approve Purchase Order"
          }
          loading={isLoading}
          actionText={
            confirmationType === "delete" ? "Delete" : grnApproval ? "Approve GRN" : "Approve"
          }
        />
      )}
      {openTransactionModel && (
        <TransactionDetailModal
          title="Transaction Details"
          transactions={generalLedger.transactionData || []}
          onClose={() => setOpenTransactionModel(false)}
          debitTotal={generalLedger.transactionDebitTotal || 0}
          creditTotal={generalLedger.transactionCreditTotal || 0}
          // PrintComponent={UnitRecModal}
          // printProps={{ Data: printData }}
          columns={[
            {
              key: "date",
              label: "Submit On",
              accessor: (t) => moment(t?.EntryDate?.slice(0, 10)).format("DD-MMM-YYYY"),
            },
            {
              key: "account",
              label: "Account",
              accessor: (t) => t?.Account?.AccountTitle,
            },
            {
              key: "narration",
              label: "Narration",
              accessor: (t) => t?.Narration,
            },
          ]}
        />
      )}
    </div>
  );
}
